<template>
  <v-dialog v-model="localDialog" width="40%" @update:modelValue="updateDialog" transition="" persistent :open-delay="0"
    :close-delay="0">
    <v-card>
      <v-card-title class="text-h6">
        <span>{{ isEntryMode ? "Add Project Budget" : "Update Project Budget" }}</span>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-form @submit.prevent="submit" ref="ProjectBudgetSetup">
          <v-row>
            <v-col cols="12">
              <v-autocomplete density="compact" variant="outlined" bg-color="grey-lighten-4"
                v-model="project_id.value.value" :error-messages="project_id.errorMessage.value" clearable
                :items="getProjectList" :item-title="item => item ? `${item.code} - ${item.project_title}` : ''"
                autocomplete="off" item-value="id" @update:model-value="onProjectChange">
                <template #label>
                  Project Name <span class="text-red-accent-4">*</span>
                </template>
              </v-autocomplete>

              <v-text-field v-model="project_code.value.value" density="compact" variant="outlined"
                bg-color="grey-lighten-4" :error-messages="project_code.errorMessage.value" readonly>
                <template #label>
                  Project Code
                </template>
              </v-text-field>

              <v-text-field v-model="project_total_budget.value.value" density="compact" variant="outlined"
                bg-color="grey-lighten-4" type="number" step="0.01"
                :error-messages="project_total_budget.errorMessage.value" clearable>
                <template #label>
                  Project Total Budget <span class="text-red-accent-4">*</span>
                </template>
              </v-text-field>
              <v-date-input v-model="budget_date.value.value" prepend-icon="" prepend-inner-icon="mdi-calendar"
                density="compact" variant="outlined" bg-color="grey-lighten-4" autocomplete="off" clearable
                :error-messages="budget_date.errorMessage.value">
                <template #label>
                  Budget Date <span class="text-red-accent-4">*</span>
                </template>
              </v-date-input>

              <v-select v-model="budget_status.value.value" :items="budgetStatusOptions"
                :error-messages="budget_status.errorMessage.value" density="compact" variant="outlined"
                bg-color="grey-lighten-4" item-title="text" item-value="value">
                <template #label>
                  Budget Status <span class="text-red-accent-4">*</span>
                </template>
              </v-select>

              <v-checkbox label="Active" v-model="status.value.value" :error-messages="status.errorMessage.value"
                density="compact" variant="outlined" hide-details single-line color="primary" :true-value="1" :false-value="0"></v-checkbox>
              <input v-model="budget_id.value.value" type="hidden" />
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <v-card-actions>
            <v-layout class="justify-end">
              <v-btn class="bg-red" variant="flat" type="reset" @click="closeDialog">Close</v-btn>
              <v-btn class="bg-brand w-15 ms-2" variant="flat" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-layout>
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { ref, watch } from "vue";
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";

export default {
  name: "ProjectBudgetSetup",
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => ({
        project_id: "",
        project_code: "",
        project_total_budget: "",
        budget_date: "",
        budget_status: "draft",
        status: "1",
        budget_id: null,
      }),
    },
  },
  data() {
    return {
      localDialog: this.dialog,
      selectedImage: null,
      errorMessages: "",
      loading: false,
      isEntryMode: true,
      budgetStatusOptions: [
        { text: "Draft", value: "draft" },
        { text: "Approved", value: "approved" },
      ],
    };
  },
  computed: {
    getProjectList() {
      return this.$store.getters.getProjectList;
    },
  },
  watch: {
    dialog(val) {
      this.localDialog = val;
    },
    localDialog(val) {
      this.$emit("update:dialog", val);
    },
    data: {
      handler(newData) {
        this.isEntryMode = !newData.id;
        this.resetForm({ errors: {}, touched: {} });
        this.project_id.setValue(newData.project_id || "", false);
        this.project_code.setValue(newData.project_code || "", false);
        this.project_total_budget.setValue(newData.project_total_budget || "", false);
        this.budget_status.setValue(newData.budget_status || "draft", false);
        this.budget_date.setValue(newData.budget_date || "", false);
        this.status.setValue(String(newData.status) || "1", false);
        this.budget_id.setValue(newData.id || "", false);

      },
    },
  },
  created() {
    if (this.$store.getters.getProjectList.length === 0) {
      this.$store.dispatch("getProjectList");
    }
  },
  methods: {
    updateDialog(val) {
      this.$emit("update:dialog", val);
    },
    closeDialog() {
      this.$emit("update:dialog", false);
    },
    onProjectChange(projectId) {
      if (!projectId) {
        this.project_code.value.value = "";
        return;
      }
      const projectList = this.getProjectList || [];
      const selectedProject = projectList.find(project => project.id == projectId);
      if (selectedProject) {
        this.project_code.value.value = selectedProject.code;
      } else {
        this.project_code.value.value = "";
      }
    },
  },
  setup(props, { emit }) {
    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        project_id: yup.number().required().label("Project Name"),
        project_code: yup.string().nullable().label("Project Code"),
        project_total_budget: yup.number().required().min(0).label("Project Total Budget"),
        budget_date: yup.date().required().label("Budget Date"),
        budget_status: yup.string().required().oneOf(["draft", "approved"]).label("Budget Status"),
      }),
    });

    const project_id = useField("project_id");
    const project_code = useField("project_code");
    const project_total_budget = useField("project_total_budget");
    const budget_status = useField("budget_status");
    const budget_date = useField("budget_date");
    const status = useField("status");
    const budget_id = useField("budget_id");
    const errorMessages = ref("");
    const loading = ref("");
    const store = useStore();

    const submit = handleSubmit((values) => {
      // Include budget_master_id for update operations
      const submitData = {
        ...values,
        status: String(values.status),
        budget_master_id: values.budget_id || null // Use budget_id as budget_master_id
      };
      store
        .dispatch("saveUpdateProjectBudget", submitData)
        .then((response) => {
          resetForm();
          emit("update:dialog", false);
          toast.success(response.data.message);
          loading.value = true;
          store
            .dispatch("getProjectBudgetList")
            .then((data) => {
              loading.value = false;
            })
            .catch((error) => {
              loading.value = false;
              toast.error(error.response.data.message);
            });
        })
        .catch((error) => {
          toast.error(error.response.data.message);
        });
    });
    return {
      submit,
      project_id,
      project_code,
      project_total_budget,
      budget_status,
      budget_date,
      status,
      errorMessages,
      budget_id,
      resetForm,
      loading,
    };
  },
  mounted() { },
};
</script>